import React, { useLayoutEffect, useRef, useState } from 'react'
import { SxProps, Theme } from '@mui/material/styles'
import Input from '@mui/material/Input'
import { InputBaseProps } from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type StatInputProps = {
  /**
   * The fontSize applied to the input
   * @default medium
   */
  fontSize?: 'small' | 'medium' | 'large'
  /**
   * If `true`, the input will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  inputProps?: InputBaseProps['inputProps']
  /**
   * The label content
   */
  label: string
  /**
   * If `true`, a `textarea` element is rendered instead of an input.
   * @default false
   */
  multiline?: boolean
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  onBlur?: InputBaseProps['onBlur']
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /**
   * It prevents the user from changing the value of the field
   * (not from interacting with the field).
   * @default false
   */
  readOnly?: boolean
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value?: any
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   * See the [`sx` page](https://mui.com/system/the-sx-prop/) for more details.
   */
  sx?: SxProps<Theme>
}

export function StatInput(props: StatInputProps) {
  const {
    fontSize = 'medium',
    fullWidth = false,
    inputProps,
    label,
    multiline = false,
    name,
    onBlur,
    onChange,
    readOnly = false,
    value,
    sx = []
  } = props
  const labelElRef = useRef<HTMLDivElement | null>(null)

  const [enableTooltip, setEnableTooltip] = useState<boolean>(false)

  useLayoutEffect(() => {

    function handleWindowResize() {
      if (!labelElRef.current) return
      const isEllipsisActive = labelElRef.current.offsetWidth < labelElRef.current.scrollWidth
      if (enableTooltip !== isEllipsisActive) setEnableTooltip(isEllipsisActive)
    }

    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Paper
      sx={[
        { padding: '24px' },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      <Tooltip disableHoverListener={!enableTooltip} placement="top" title={label}>
        <Typography
          ref={labelElRef}
          variant="h6"
          component="div"
          sx={{
            color: colors.gray500,
            fontSize: '14px',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      <Input
        disableUnderline={readOnly}
        fullWidth={fullWidth}
        inputProps={inputProps}
        multiline={multiline}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        readOnly={readOnly}
        value={value !== null ? value : ''}
        sx={[
          {
            color: colors.gray900,
            fontWeight: 600
          },
          fontSize === 'small' && { fontSize: '18px' },
          fontSize === 'medium' && { fontSize: '24px' },
          fontSize === 'large' && { fontSize: '30px' }
        ]}
      />
    </Paper>
  )
}

export default StatInput